import type { EmployeeDevice } from './api/admin'
import { kstDotDateStr } from '@biznomi/ui-react'

/**
 * 기기 승인 확인창 문구 — **한 곳에서만 만든다.**
 *
 * 승인하는 자리가 둘이다 (직원 상세 · 승인 대기 목록). 두 화면이 각자 문장을 만들면
 * 다음에 한쪽만 고쳐진다. 오늘까지 그 모양을 세 번 겪었다
 * (사번 매칭 사본 · decodeUploadFileName · 초기 비밀번호 규칙).
 */

/** 승인하면 꺼질 기기 — 1기기 1사용자라 서버가 자동으로 끈다 (`device.service.ts:138`) */
export function willBeTurnedOff(target: EmployeeDevice, devices: EmployeeDevice[]) {
  return devices.filter((x) => x.id !== target.id && x.status === 'APPROVED' && x.isActive)
}

/**
 * 🔴 «꺼집니다» 는 사실이 아니었다 (2026-09-10 코드로 잼).
 *
 * `isActive:false` 가 되면 이렇게 된다:
 * ```
 * NFC 태깅         ❌ verifyDevice 가 막는다 → 403
 * 셀프 비번 재설정   ❌ auth.controller:189 가 APPROVED+isActive 를 요구한다
 * 로그인 세션       ✅ 안 끊긴다 (JWT 는 기기 레코드와 무관하다)
 * 화면 보기         ✅ 내 기록·급여명세서 다 보인다
 * ```
 * 옛 폰은 **앱이 멀쩡히 열리고 로그인도 유지되는데 찍으려는 순간에만 막힌다.**
 * «꺼진다» 로 안내받은 관리자는 옛 폰이 못 쓰게 된 줄 알고 직원에게 그렇게 전하고,
 * 직원은 앱이 열리니 괜찮은 줄 안다. 둘 다 다음 날 아침 입구에서 알게 된다.
 *
 * 🔴 꺼질 기기가 **없을 때**가 더 중요하다. 그건 «해제·반려 뒤 재등록» 이라는 뜻이고,
 *   `device.service` 의 «분실한 폰을 주운 사람이 재등록» 방어는 **관리자가 맥락을 보고
 *   판단한다**는 전제 위에 서 있다. 지금까지 그 맥락이 화면에 없었다 —
 *   «대기 중» 만 보이면 관리자는 그냥 눌러 준다.
 */
export function approveMessage(
  employeeName: string,
  target: EmployeeDevice,
  devices: EmployeeDevice[],
): string {
  const name = target.deviceName || '이 폰'
  const off = willBeTurnedOff(target, devices)
  const head = `${name}(으)로 ${employeeName} 님이 출퇴근을 찍을 수 있게 됩니다.`

  if (off.length > 0) {
    const names = off.map((x) => x.deviceName || '이름 없는 기기').join(' · ')
    return `${head} 쓰던 기기(${names})로는 출퇴근을 찍을 수 없게 됩니다. 로그인은 유지되고 기록 조회는 그대로 됩니다. 한 사람이 한 대만 씁니다.`
  }

  /*
   * 지금 활성 기기가 없다 = 전에 해제·반려된 뒤 다시 등록한 것이다.
   * ⚠ 해제 «사유» 는 응답에 없다. 있는 것처럼 말하지 않는다 — 시각까지만 적고
   *   나머지는 감사 로그에서 보라고 한다.
   */
  const revoked = devices
    .concat(target)
    .filter((x) => x.revokedAt)
    .sort((a, b) => (a.revokedAt! < b.revokedAt! ? 1 : -1))[0]
  const when = revoked?.revokedAt ? ` (마지막 해제 ${kstDotDateStr(revoked.revokedAt)})` : ''
  return `${head} 지금 쓰는 기기가 없습니다 — 전에 해제되었거나 반려된 뒤 다시 등록한 것입니다${when}. 언제·왜 그랬는지 확인하고 승인해 주세요.`
}
